import { useBlocNext } from "@blac/react";
import styled from "@emotion/styled";
import type { FC, ReactNode } from "react";
import React, { useEffect } from "react";
import type { SignupCustomStep } from "src/ui/components/SignupCustomContent/SignupCustom.types";
import SignupCustomBloc from "src/ui/components/SignupCustomContent/state/SignupCustomBloc";
import Translate from "src/ui/components/Translate/Translate";
import WithBackground from "components/WithBackground/WithBackground";
import { Button } from "@9amhealth/shared";

const CenterEl = styled.div`
  border-radius: 0.6em;
  width: min(90vw, 580px);
  text-align: center;
  margin: 0 auto;
  height: 100%;

  display: grid;
  place-items: center;

  button {
    margin-top: 1em;
    font-size: 1em;
    padding: 0.6em 1.2em;
  }

  a {
    color: var(--color-charcoal-60);
  }

  [data-nine-modal] & {
    width: 100%;
  }

  h4 {
    text-wrap: pretty;
  }

  .ineligibility-description {
    text-wrap: balance;
  }
`;

export enum IneligibleIcon {
  DOCTOR = "doctor",
  FORM_EDIT = "form-edit",
  FORM_X = "form-x"
}

const ineligibleIcons: Record<IneligibleIcon, ReactNode> = {
  [IneligibleIcon.DOCTOR]: (
    <svg xmlns="http://www.w3.org/2000/svg" width="64" height="65" fill="none">
      <path
        fill="#212121"
        d="M44.958 1.592H19.044a3.79 3.79 0 0 0-3.79 3.79v54.235a3.79 3.79 0 0 0 3.79 3.79h25.914a3.79 3.79 0 0 0 3.79-3.79V5.382a3.79 3.79 0 0 0-3.79-3.79Zm-5.525 41.321v-6.69l2.595.98a105.759 105.759 0 0 1-1.894 6.136 6.416 6.416 0 0 0-.7-.426ZM19.044 3.664h25.914c.95 0 1.719.77 1.719 1.718v2.452l-.038-.102c-.603 10.868-2.15 19.974-4.074 27.46l-4.035-1.524a1.718 1.718 0 0 1-1.111-1.608v-.164a7.5 7.5 0 0 0 1.285-1.618c.32-.54.58-1.116.803-1.614.08-.176.154-.341.225-.492.293-.62.584-1.126 1.025-1.567 1.134-1.134 1.46-2.368 1.262-3.482a3.808 3.808 0 0 0-.435-1.199c.316-.309.558-.706.673-1.182.545-2.273.612-6.351-3.044-8.567-2.256-1.368-3.945-2.116-5.645-2.254-1.702-.14-3.278.348-5.239 1.092-.287.109-.585.217-.886.326-.654.237-1.32.478-1.911.742-.894.4-1.771.916-2.46 1.722-1.378 1.61-1.723 4.023-.991 7.865-.417.863-.542 1.694-.449 2.46.105.858.476 1.562.892 2.083.452.567.773.976.935 1.425.748 2.077 1.81 3.57 2.97 4.62a1.716 1.716 0 0 1-1.127 1.38l-7.977 2.828V5.382c0-.949.77-1.718 1.718-1.718h.001Zm6.364 23.27c-.284-.789-.808-1.444-1.2-1.934l-.065-.08a2.08 2.08 0 0 1-.454-1.043c-.038-.31-.007-.696.19-1.162l.5-.124a1.607 1.607 0 0 0 1.162-1.972 2.75 2.75 0 0 1 .735-.842c.046-.032.082-.054.107-.068 2.928-.155 6.443-1.636 7.726-2.215.196-.089.338-.09.427-.066.551.15 1.408.462 2.139 1.033.708.555 1.278 1.334 1.378 2.487.068.782.672 1.571 1.536 1.709.175.218.335.51.392.828.07.389.013.954-.687 1.654-.691.69-1.104 1.448-1.434 2.147-.106.224-.2.433-.29.633-.205.459-.39.869-.646 1.3-.65 1.091-1.743 2.252-4.766 3.14-.104.03-.181.042-.235.044-1.542.046-4.86-.879-6.514-5.469Zm-.762-11.785c.406-.475.97-.836 1.733-1.178.522-.233 1.065-.43 1.673-.65.318-.115.653-.236 1.012-.372 1.95-.74 3.14-1.062 4.337-.964 1.2.098 2.545.63 4.739 1.96 2.468 1.496 2.58 4.323 2.103 6.313a.427.427 0 0 1-.158.23c-.228-1.694-1.133-2.874-2.134-3.657-1.034-.81-2.184-1.213-2.867-1.4-.666-.184-1.31-.059-1.827.175-1.314.593-4.555 1.93-7.056 2.038-.48.021-.894.279-1.141.458-.29.21-.57.478-.808.758-.146.171-.288.361-.412.562-.232-2.378.198-3.563.805-4.272v-.001Zm.671 34.189a1.042 1.042 0 1 1-2.084 0 1.042 1.042 0 0 1 2.084 0Zm-7.991 10.28V38.661l6.048-2.144v9.84a3.114 3.114 0 1 0 2.072.096v-10.67l.55-.194a3.787 3.787 0 0 0 2.213-2.074c1.382.74 2.75.989 3.776.958.28-.008.54-.063.758-.127 1.115-.328 2.03-.702 2.794-1.111a3.788 3.788 0 0 0 1.825 2.17v7.386a6.702 6.702 0 0 0-2.95 2.082c-1.203 1.462-1.998 3.628-1.77 6.654a1.58 1.58 0 0 0 1.15 1.397c.24.069.513.148.741.218.115.035.212.067.287.093l.088.032-.014-.007.926-1.853a5.275 5.275 0 0 0-.674-.244 26.21 26.21 0 0 0-.459-.137c-.11-2.366.541-3.885 1.325-4.837.738-.898 1.642-1.352 2.295-1.522.32.144.707.383 1.097.727-3.042 8.25-6.263 13.434-8.1 15.941H19.045c-.95 0-1.718-.769-1.718-1.718h-.002Z"
      />
    </svg>
  ),
  [IneligibleIcon.FORM_EDIT]: (
    <svg xmlns="http://www.w3.org/2000/svg" width="64" height="65" fill="none">
      <path
        fill="#212121"
        d="M34.6 39.6 33 41.3l1.7-1.7ZM32.9 47.3v1.8H16.6v-1.8h16.3ZM30.5 41.3l1.7-1.7H16.6v1.7h13.9ZM16.6 33.6H39l.2-1.8H16.6v1.8ZM24 18.4c-.4.4-1 .7-1.7.7s-1.3-.3-1.7-.7c-.5-.5-.8-1-.8-1.8 0-.7.3-1.3.8-1.7.4-.5 1-.8 1.7-.8s1.3.3 1.8.8c.4.4.7 1 .7 1.7s-.3 1.3-.7 1.8ZM16.6 25.6v-.2c0-1 .5-1.7 1.5-2.2 1-.5 2.4-.8 4.2-.8 1.8 0 3.2.3 4.2.8s1.5 1.2 1.5 2.2v.2H16.6Z"
      />
      <path
        fill="#212121"
        fillRule="evenodd"
        d="M10.7 11.4c0-1.6 1.3-3 2.9-3h27c.8 0 1.6.4 2.1 1l10.2 10c.5.5.8 1.3.8 2V25c.5.2.9.5 1.2.8l1.2 1.2a4.6 4.6 0 0 1 0 6.5L53.7 36v18.2c0 1.6-1.2 2.9-2.8 2.9H13.6a2.9 2.9 0 0 1-3-3V11.5ZM35.2 49H33v-7.8l1.7-1.7 6-6 .2-1.8c.4-4.2.4-17.2.3-21.5H13.6c-.7 0-1.2.4-1.2 1v42.8c0 .6.5 1.1 1.2 1.1h15.3c2.6-1.7 4.7-3.8 6.3-6.1Zm11.9-22 1.3-1.3-1.3 1.3Zm-4.3-9V12l7.5 7.4H44c-.6 0-1.2-.6-1.2-1.2Zm10.9 9 1.2 1.1.4.6A3.6 3.6 0 0 1 52 32l-3.7-3.8 1.3-1.3c1.2-1.1 3-1.1 4 0ZM39.5 43c3.1-.5 9-5.2 10.7-10.4L47 29.6 36.5 40.2a2.2 2.2 0 0 1 1 2 2.2 2.2 0 0 1 2 1Zm.2 1.3Zm-2.9-3.8Zm-1.3.6-.9.8v5.3H40l.8-.8a1 1 0 0 0-.2-.6 1 1 0 0 0-1.3 0l-.1.1-1-1h.1c.4-.4.4-1 0-1.3a1 1 0 0 0-1.3 0l-1-.9c.4-.4.4-1 0-1.4a1 1 0 0 0-.4-.2Z"
        clipRule="evenodd"
      />
    </svg>
  ),
  [IneligibleIcon.FORM_X]: (
    <svg xmlns="http://www.w3.org/2000/svg" width="64" height="65" fill="none">
      <path
        fill="#212121"
        d="M40 31.9a1 1 0 0 0-1.4 1.4l2.7 2.8-2.8 2.8a1 1 0 0 0 1.5 1.4l2.8-2.8 2.8 2.8a1 1 0 1 0 1.4-1.4L44.2 36l2.8-2.8a1 1 0 0 0-1.5-1.4l-2.7 2.8-2.8-2.8Z"
      />
      <path
        fill="#212121"
        fillRule="evenodd"
        d="M43.4 9.7V7c0-.8-.7-1.5-1.5-1.5h-3c-1-2-3.6-3.2-6.2-3.4-2.6-.2-5.5.7-7.1 3.4h-4c-.9 0-1.6.7-1.6 1.5v2.7h-7.5c-.8 0-1.5.7-1.5 1.5v43.6c0 .8.7 1.5 1.5 1.5h39.1c.8 0 1.5-.7 1.5-1.5V47a15 15 0 0 0 0-21.8v-14c0-.8-.7-1.5-1.5-1.5h-8.2Zm2.4 11.7c1-3 1.6-6.4 2-10h-4.4v2c0 1-.7 1.6-1.5 1.6H21.5c-.8 0-1.5-.7-1.5-1.5v-2h-7.3v43h13.1l2.5-3.1 3.7-4.8A15 15 0 0 1 42.8 21c1 0 2 0 3 .3ZM27 6.4c-.3.5-.7.8-1.3.8h-4v6H37c1.4-1.1 3.7-3.2 4.4-5.8v-.2h-2.7c-.6 0-1-.4-1.3-.8-.7-1.3-2.6-2.4-4.8-2.6-2.2-.1-4.4.6-5.6 2.7Zm2.8 29.7a13 13 0 1 1 26 0 13 13 0 0 1-26 0Z"
        clipRule="evenodd"
      />
    </svg>
  )
};

const SignupCustomIneligibility: FC<{
  resolveStep?: SignupCustomStep;
  hideButton?: boolean;
  buttonText?: ReactNode;
  children?: ReactNode;
  icon?: IneligibleIcon;
}> = ({ resolveStep, hideButton, children, buttonText, icon }) => {
  const [, { __unsafe__forceStep, hideSaving }] = useBlocNext(SignupCustomBloc);

  useEffect(() => {
    hideSaving();
  }, [hideSaving]);

  const redoStep = (): void => {
    if (resolveStep) {
      __unsafe__forceStep(resolveStep);
    }
  };

  return (
    <WithBackground variant="animate-primary">
      <CenterEl>
        <div style={{ padding: "2em 0" }}>
          <nine-heading>
            {icon && ineligibleIcons[icon]}
            {children}
          </nine-heading>
          {hideButton
            ? null
            : resolveStep && (
                <>
                  <nine-spacer s="md"></nine-spacer>
                  <Button onPress={redoStep} theme="dusk" center>
                    {buttonText ?? (
                      <Translate msg="ineligibility.medical.button" />
                    )}
                  </Button>
                </>
              )}
        </div>
      </CenterEl>
    </WithBackground>
  );
};

export default SignupCustomIneligibility;
